import { Dropdown } from "semantic-ui-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { toPng } from "html-to-image";
import { geiCodes, kgToTons } from "../../../../utils/dataAndFormulas/GEIForms";
import { groupThousands } from "../../../../utils/numberFormat";
import { drawGEICustomCharts } from "./pdfGEICharts";

// ── Helpers ────────────────────────────────────────────────────────────────

const getNeededDecimals = (value) => {
  if (typeof value !== "number" || isNaN(value) || value === 0) return 2;
  const abs = Math.abs(value);
  if (abs < 0.0000005) return -1;
  if (abs >= 1) return 2;
  const leadingZeros = Math.floor(-Math.log10(abs));
  return Math.max(2, leadingZeros + 2);
};

const getRowDecimalPlaces = (values) => {
  let maxDecimals = 2;
  for (const v of values) {
    if (typeof v !== "number" || isNaN(v) || v === 0) continue;
    const needed = getNeededDecimals(v);
    if (needed === -1) continue;
    if (needed > maxDecimals) maxDecimals = needed;
  }
  return Math.min(maxDecimals, 4);
};

// AJUSTES DE MAGNITUDES NUMÉRICAS — Regla 1/2/2+: separador de miles
// agrupado con groupThousands() de utils/numberFormat.js (misma lógica
// que el resto de la app). OJO: acá se le pasa " " (espacio común) en
// vez del espacio fino por defecto (U+202F) — las fuentes estándar de
// jsPDF (Helvetica) usan codificación WinAnsi, que no tiene ese
// carácter y lo rompe (se veía como "/" en el PDF). Mismo tipo de
// límite que ya existía documentado acá abajo para "₂" de tCO₂e.
// Antes esta función tampoco agrupaba miles y usaba coma decimal; se
// pasa a punto decimal para quedar consistente con numberFormat.js, que
// ya es el estándar en los otros 33+ dashboards. Los decimales por fila
// (2 a 4, adaptativos según la magnitud más chica de esa fila — ver
// getRowDecimalPlaces) se mantienen: son una extensión razonable de la
// Regla 2+ para que las columnas de una tabla de años queden alineadas,
// no algo que las reglas de miles/decimales prohíban.
const PDF_THOUSANDS_SEPARATOR = " ";

const formatVal = (value, decimals) => {
  if (typeof value !== "number" || isNaN(value)) return "-";
  if (value === 0) return "-";

  const isNegligible = Math.abs(value) < 0.0000005;
  const magnitude = isNegligible ? 0 : Math.abs(value);
  const sign = !isNegligible && value < 0 ? "-" : "";
  const fixed = magnitude.toFixed(decimals);
  const [intPart, decPart] = fixed.split(".");
  const groupedInt = groupThousands(intPart, PDF_THOUSANDS_SEPARATOR);
  return decPart !== undefined ? `${sign}${groupedInt}.${decPart}` : `${sign}${groupedInt}`;
};

const getScopeTotal = (entry) => {
  if (!entry) return 0;
  return (entry.scope1 || 0) + (entry.scope2 || 0) + (entry.scope3 || 0);
};

const getPillColor = (val, prevVal) => {
  if (val == null || val === 0) return null;
  if (prevVal == null || prevVal === 0) return [208, 208, 208];
  if (val > prevVal) return [244, 168, 164];
  if (val < prevVal) return [168, 213, 181];
  return [208, 208, 208];
};

// ── Componente ────────────────────────────────────────────────────────────

const STD_LABEL = {
  defra:    "DEFRA 2025",
  ghg_ar6:  "GHG Protocol AR6",
  ipcc_ar6: "IPCC AR6",
};

const GEIPDFReport = ({
  conceptos = [],
  years = [],
  totales = {},
  totalesScopes = {},
  valueBaseline = {},
  filters = {},
  indicatorsLineBaseGEI = [],
  t = (k) => k,
  summaryRef,
  tableGrafRef,
  chartCardRefs = [],
  siteName = "",
  factorsMeta = null,
}) => {
  const generatePDF = async (includeCharts = false) => {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let yOffset = 36;

    // ── Encabezado ─────────────────────────────────────────────────────────
    doc.setFontSize(14);
    doc.setFont(undefined, "bold");
    doc.setTextColor(20, 40, 80);
    doc.setCharSpace(1.5);
    doc.text("Reporte GEI", margin, yOffset);
    doc.setCharSpace(0);
    yOffset += 16;

    if (siteName) {
      doc.setFontSize(10);
      doc.setFont(undefined, "normal");
      doc.setTextColor(60, 80, 120);
      doc.text(siteName.toLocaleUpperCase(), margin, yOffset);
      yOffset += 14;
    }

    doc.setFontSize(8);
    doc.setFont(undefined, "normal");
    doc.setTextColor(130, 130, 130);
    doc.setCharSpace(0.8);
    doc.text(
      `Generado el ${new Date().toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })}`,
      margin,
      yOffset
    );
    doc.setCharSpace(0);
    yOffset += 18;

    // ── Metadata de factores activos ───────────────────────────────────────
    if (factorsMeta) {
      const parts = [];
      if (factorsMeta.standard_key)
        parts.push(`Estándar: ${STD_LABEL[factorsMeta.standard_key] || factorsMeta.standard_key}`);
      if (factorsMeta.organism)
        parts.push(`Organismo: ${factorsMeta.organism}`);
      if (factorsMeta.data_year)
        parts.push(`Año dato: ${factorsMeta.data_year}`);
      if (factorsMeta.verified_at)
        parts.push(`Verificado: ${factorsMeta.verified_at}`);
      if (factorsMeta.country_code)
        parts.push(`País electricidad: ${factorsMeta.country_code}`);

      if (parts.length) {
        doc.setFontSize(7.5);
        doc.setFont(undefined, "normal");
        doc.setTextColor(90, 110, 150);
        doc.text(parts.join("  ·  "), margin, yOffset);
        yOffset += 12;
      }
    }

    doc.setDrawColor(210, 210, 210);
    doc.line(margin, yOffset, pageWidth - margin, yOffset);
    yOffset += 12;

    // ── Gráficos opcionales ────────────────────────────────────────────────
    const imgWidth = pageWidth - margin * 2;

    const captureElement = async (ref) => {
      if (!ref?.current) return null;
      try {
        return await toPng(ref.current, {
          pixelRatio: 2,
          filter: (node) => node.dataset?.pdfExclude !== "true",
        });
      } catch {
        return null;
      }
    };

    const addImageToPDF = (img) => {
      if (!img) return;
      const imgProps = doc.getImageProperties(img);
      const naturalH = (imgProps.height * imgWidth) / imgProps.width;
      const maxH = pageHeight - margin * 2;
      let drawWidth = imgWidth;
      let drawHeight = naturalH;
      if (naturalH > maxH) {
        drawHeight = maxH;
        drawWidth = imgWidth * (maxH / naturalH);
      }
      const remaining = pageHeight - margin - yOffset;
      if (drawHeight > remaining) {
        if (remaining >= 120) {
          const scale = remaining / drawHeight;
          drawWidth *= scale;
          drawHeight = remaining;
        } else {
          doc.addPage();
          yOffset = margin;
        }
      }
      doc.addImage(img, "PNG", margin, yOffset, drawWidth, drawHeight);
      yOffset += drawHeight + 10;
    };

    if (includeCharts) {
      const summaryImg = await captureElement(summaryRef);
      if (summaryImg) addImageToPDF(summaryImg);

      if (chartCardRefs.length === 3) {
        yOffset = await drawGEICustomCharts(
          doc,
          { margin, pageWidth, pageHeight, imgWidth, yOffset },
          { totales, totalesScopes, valueBaseline, filters, t },
        );
      } else {
        const tableGrafImg = await captureElement(tableGrafRef);
        if (tableGrafImg) addImageToPDF(tableGrafImg);
      }
    }

    if (yOffset + 60 > pageHeight - margin) {
      doc.addPage();
      yOffset = margin;
    }

    // ── Línea base por alcance ─────────────────────────────────────────────
    const alcance1 = indicatorsLineBaseGEI.find((v) => v.name === "alcance_1_kpis");
    const alcance2 = indicatorsLineBaseGEI.find((v) => v.name === "alcance_2_kpis");
    const alcance3 = indicatorsLineBaseGEI.find((v) => v.name === "alcance_3_kpis");

    const sortedYears = [...years].sort((a, b) => Number(b) - Number(a));
    const yearKeys = sortedYears.map(String);

    // ── rawValues para pills ───────────────────────────────────────────────
    const rawValues = conceptos.map((item) => {
      const vals = {};
      sortedYears.forEach((yr) => {
        vals[String(yr)] = kgToTons(getScopeTotal(totales[item]?.[yr]));
      });
      return vals;
    });

    // ── Filas ──────────────────────────────────────────────────────────────
    const tableRows = conceptos.map((item) => {
      const rowValues = sortedYears
        .map((yr) => kgToTons(getScopeTotal(totales[item]?.[yr])))
        .filter((v) => typeof v === "number" && !isNaN(v));

      const baseDataA1 = totales[item]?.[alcance1?.yearBaseline] || {};
      const baseDataA2 = totales[item]?.[alcance2?.yearBaseline] || {};
      const baseDataA3 = totales[item]?.[alcance3?.yearBaseline] || {};
      const lbTotal = kgToTons(
        (baseDataA1.scope1 || 0) +
        (baseDataA2.scope2 || 0) +
        (baseDataA3.scope3 || 0)
      );
      if (lbTotal) rowValues.push(lbTotal);

      const rowDecimals = getRowDecimalPlaces(rowValues);

      const hasS1 =
        sortedYears.some((y) => (totales[item]?.[y]?.scope1 || 0) !== 0) ||
        (baseDataA1.scope1 || 0) !== 0;
      const hasS2 =
        sortedYears.some((y) => (totales[item]?.[y]?.scope2 || 0) !== 0) ||
        (baseDataA2.scope2 || 0) !== 0;
      const hasS3 =
        sortedYears.some((y) => (totales[item]?.[y]?.scope3 || 0) !== 0) ||
        (baseDataA3.scope3 || 0) !== 0;
      const alcanceText = [hasS1 && "A1", hasS2 && "A2", hasS3 && "A3"]
        .filter(Boolean)
        .join(" ");

      const row = {
        code: geiCodes[item] || "-",
        indicator: t(item) || item,
        alcance: alcanceText,
      };

      sortedYears.forEach((yr) => {
        const val = kgToTons(getScopeTotal(totales[item]?.[yr]));
        row[String(yr)] = formatVal(val, rowDecimals);
      });

      row.lb = formatVal(lbTotal, rowDecimals);
      // "₂" (subíndice Unicode) no existe en la codificación WinAnsi de las
      // fuentes estándar de jsPDF — corrompe este texto y todo lo que sigue
      // en la misma celda. Se usa "2" normal, solo en el PDF (en pantalla
      // se sigue mostrando "tCO₂e" tal cual, ver GEISummary.js/GEIDashBoard.js).
      row.units = "tCO2e";

      return row;
    });

    const tableColumns = [
      { header: t("code") || "Código",       dataKey: "code" },
      { header: t("indicators") || "Indicador", dataKey: "indicator" },
      { header: t("scope") || "Alcance",     dataKey: "alcance" },
      ...sortedYears.map((y) => ({ header: String(y), dataKey: String(y) })),
      { header: t("LB") || "LB",             dataKey: "lb" },
      { header: t("units") || "Unidad",      dataKey: "units" },
    ];

    autoTable(doc, {
      startY: yOffset,
      columns: tableColumns,
      body: tableRows,
      styles: {
        fontSize: 7,
        cellPadding: { top: 5, right: 4, bottom: 5, left: 4 },
        halign: "center",
        valign: "middle",
        fillColor: [237, 235, 235],
        textColor: [40, 40, 40],
        lineColor: [255, 255, 255],
        lineWidth: 0.5,
      },
      headStyles: {
        fillColor: [237, 237, 237],
        textColor: [237, 237, 237],
        fontStyle: "bold",
        fontSize: 6,
        lineWidth: 0,
        cellPadding: { top: 5, right: 3, bottom: 5, left: 3 },
      },
      columnStyles: {
        code:      { halign: "left", cellWidth: 38 },
        indicator: { halign: "left" },
        alcance:   { cellWidth: 32 },
        units:     { halign: "left", cellWidth: 45 },
      },
      theme: "plain",
      margin: { left: margin, right: margin },
      didParseCell: (data) => {
        if (data.section !== "body") return;
        const yr = data.column.dataKey;
        if (!yearKeys.includes(yr)) return;
        const rowVals = rawValues[data.row.index];
        if (!rowVals) return;
        const val = rowVals[yr];
        const yrIdx = sortedYears.findIndex((y) => String(y) === yr);
        const prevYr = yrIdx < sortedYears.length - 1 ? sortedYears[yrIdx + 1] : null;
        const prevVal = prevYr != null ? rowVals[String(prevYr)] : null;
        if (getPillColor(val, prevVal)) {
          data.cell.styles.textColor = [237, 235, 235];
        }
      },
      didDrawCell: (data) => {
        if (data.section === "head") {
          const { x, y, width, height } = data.cell;
          const text = String(data.cell.raw || "");
          if (!text) return;
          doc.setFontSize(6);
          doc.setFont(undefined, "bold");
          doc.setTextColor(40, 40, 40);
          doc.setCharSpace(0.8);
          doc.text(text, x + width / 2, y + height / 2 + 3, { align: "center" });
          doc.setCharSpace(0);
          return;
        }
        if (data.section !== "body") return;
        const yr = data.column.dataKey;
        if (!yearKeys.includes(yr)) return;
        const rowVals = rawValues[data.row.index];
        if (!rowVals) return;
        const val = rowVals[yr];
        const yrIdx = sortedYears.findIndex((y) => String(y) === yr);
        const prevYr = yrIdx < sortedYears.length - 1 ? sortedYears[yrIdx + 1] : null;
        const prevVal = prevYr != null ? rowVals[String(prevYr)] : null;
        const color = getPillColor(val, prevVal);
        if (!color) return;

        const { x, y, width, height } = data.cell;
        const text = String(data.cell.raw || "-");
        const pillW = Math.min(width - 8, 58);
        const pillH = Math.max(height - 5, 10);
        const pillX = x + (width - pillW) / 2;
        const pillY = y + (height - pillH) / 2;

        doc.setFillColor(...color);
        doc.roundedRect(pillX, pillY, pillW, pillH, 3, 3, "F");

        doc.setFontSize(7);
        doc.setFont(undefined, "bold");
        doc.setTextColor(0, 0, 0);
        doc.text(text, pillX + pillW / 2, pillY + pillH / 2 + 2.5, {
          align: "center",
        });
      },
    });

    const safeSite = siteName ? `_${siteName.replace(/\s+/g, "_")}` : "";
    doc.save(
      includeCharts
        ? `reporte_gei${safeSite}_con_graficos_${new Date().getFullYear()}.pdf`
        : `reporte_gei${safeSite}_${new Date().getFullYear()}.pdf`
    );
  };

  const options = [
    { key: "sin", text: "Solo tabla",   icon: "table",     onClick: () => generatePDF(false) },
    { key: "con", text: "Con gráficos", icon: "chart bar", onClick: () => generatePDF(true) },
  ];

  return (
    <div data-pdf-exclude="true" style={{ display: "contents" }}>
      <Dropdown
        pointing="top right"
        icon={null}
        trigger={
          <button className="kpi-export-icon-btn" title="Descargar PDF">
            <i className="download icon" style={{ color: "#555" }} />
          </button>
        }
        options={options}
      />
    </div>
  );
};

export default GEIPDFReport;
